import { SUCCESS_RESPONSES } from '../constants/serverResponses'
import { DataDog } from '../datadog'
import { dispatchMFEAdobeEvent, dispatchMFEEvent } from './events'
import { adyenIdealAuthentication, idealAuthentication } from '../api/ideal'
import { redirect, isMobile } from './common'
import { RESPONSE_ERROR_TEXT } from './serverTextResponses'
import { paypalAuthentication } from '../api/paypal'
import { PAYMENT_METHOD_TYPES } from '../constants/paymentMethods'
import {
  adyenBancontactAuthentication,
  bancontactAuthentication,
} from '../api/bancontact'
import { prepareMetadata } from '../hooks/useMetadata'
import { trustlyAuthentication } from '../api/trustly.js'
import { vippsAuthentication } from '../api/vipps.js'
import { mobilePayAuthentication } from '../api/mobilePay.js'
import { swishAuthentication } from '../api/swish.js'
import { checkPaymentStatus } from '../api/payments'
import { paymentStatuses } from '../constants/paymentStatuses'

const actions = {
  [PAYMENT_METHOD_TYPES.IDeal]: idealAuthentication,
  [`Adyen${PAYMENT_METHOD_TYPES.IDeal}`]: adyenIdealAuthentication,
  [PAYMENT_METHOD_TYPES.PayPal]: paypalAuthentication,
  [PAYMENT_METHOD_TYPES.PayPalCredit]: paypalAuthentication,
  [PAYMENT_METHOD_TYPES.Bancontact]: bancontactAuthentication,
  [`Adyen${PAYMENT_METHOD_TYPES.Bancontact}`]: adyenBancontactAuthentication,
  [PAYMENT_METHOD_TYPES.Trustly]: trustlyAuthentication,
  [PAYMENT_METHOD_TYPES.Vipps]: vippsAuthentication,
  [PAYMENT_METHOD_TYPES.MobilePay]: mobilePayAuthentication,
  [PAYMENT_METHOD_TYPES.Swish]: swishAuthentication,
}

function buildUrl(url, data) {
  const updatedUrl = new URL(url)
  updatedUrl.searchParams.append('errorMessage', data.errorMessage)
  return redirect(updatedUrl.toString())
}

export const handlePayment = async data => {
  const {
    pageState,
    setError,
    l10n,
    setIsPaymentPending,
    handleSubmit,
    transactionDetails,
    handleAuthoriseError,
    paymentMethod,
    setSwishQrCode,
    setPaymentDisabled,
  } = data
  DataDog.info(`${paymentMethod} button clicked`)
  dispatchMFEEvent('tui-button.payment-btn-clicked', { paymentMethod })
  dispatchMFEAdobeEvent(`Pay-${paymentMethod}`)
  if (pageState.isPaymentBlocked) {
    setError(l10n.t('payment-btn-error'))
    return
  }

  const continuePayment = () => {
    let result
    if (transactionDetails.metadata === 'invalid') {
      DataDog.error(`Wrong format of metadata`)
      console.error(`Wrong format of metadata`)
    } else if (transactionDetails.metadata === 'lengthError') {
      console.error('Metadata should be not greater than 100 characters')
    }

    setError('')
    setIsPaymentPending(true)
    const runner = handleSubmit(async ({ notes }) => {
      try {
        let pm = paymentMethod
        const adyenPm = `Adyen${paymentMethod}`
        if (
          transactionDetails?.adyenPaymentMethods?.includes(paymentMethod) &&
          actions[adyenPm]
        ) {
          pm = adyenPm
        }

        const { responseData, responseStatus } = await actions[pm]({
          amount:
            transactionDetails.overrideAmount || transactionDetails.amount,
          paymentUrl: window.location.href,
          metadata: prepareMetadata(transactionDetails.metadata),
          cancelUrl:
            paymentMethod === PAYMENT_METHOD_TYPES.PayPal ||
            paymentMethod === PAYMENT_METHOD_TYPES.PayPalCredit
              ? window.location.href
              : undefined,
          notes,
        })
        if (responseStatus === SUCCESS_RESPONSES.OK) {
          window.tuiPaymentsMFEInstance?.onAuthorised?.()
          if (paymentMethod === 'Swish') {
            if (isMobile()) {
              redirect(responseData?.paymentUrl)
              return
            }
            async function makePolling(startDate, status = 'pending') {
              const validStatuses = ['pending', 'authorisedRequested']
              const interval = 3000

              if (!validStatuses.includes(status)) return

              try {
                const { responseData } = await checkPaymentStatus(
                  'swish',
                  transactionDetails?.id,
                )
                if (
                  responseData?.status === paymentStatuses.AUTHORISE_REQUESTED
                ) {
                  setPaymentDisabled(true)
                }
                if (responseData?.status === 401) {
                  return redirect(transactionDetails?.failureUrl)
                }

                const data = responseData
                switch (data.status) {
                  case 'success':
                    return redirect(transactionDetails?.successUrl)
                  case 'abort':
                    return setPaymentDisabled(false)
                  case 'error':
                    if (data.isRetryable) {
                      setPaymentDisabled(false)
                    } else {
                      buildUrl(transactionDetails?.failureUrl, data)
                    }
                    return

                  default:
                    return setTimeout(
                      () => makePolling(startDate, data.status),
                      interval,
                    )
                }
              } catch (error) {
                console.error('Polling failed:', error)
              }
            }
            makePolling(new Date())
            return setSwishQrCode(responseData?.paymentQrCodeData)
          }
          if (pm === `Adyen${PAYMENT_METHOD_TYPES.Bancontact}`) {
            result = responseData
            return
          }
          setIsPaymentPending(false)
          DataDog.info(
            `Redirection. Component: ${paymentMethod} (handleSubmit, success scenario), redirect url: ${responseData?.redirectUrl || responseData?.paymentUrl}`,
          )
          redirect(responseData?.redirectUrl || responseData?.paymentUrl)
          return
        }
        setIsPaymentPending(false)
        handleAuthoriseError(responseStatus, responseData)

        if (responseData?.isRetryable) {
          DataDog.info('Retryable error response', {
            responseData,
            responseStatus,
          })
          setError(RESPONSE_ERROR_TEXT(l10n, responseStatus))
        }
      } catch (e) {
        if (transactionDetails.failureUrl) {
          DataDog.info(
            `Redirection. Component: ${paymentMethod} (handleSubmit, failure scenario), redirect url: ${transactionDetails.failureUrl}`,
          )
          redirect(transactionDetails.failureUrl)
          return
        }
        DataDog.info(
          'Error/Catch flow, no failureUrl provided, "Technical difficulties" message has been shown',
        )

        setError(RESPONSE_ERROR_TEXT(l10n, 500))
      } finally {
        setIsPaymentPending(false)
      }
    })

    return runner().then(() => result)
  }

  const mfeElement = window.tuiPaymentsMFEInstance

  if (mfeElement?.onAuthorising) {
    DataDog.info(`onAuthorising hook has been enabled`)

    return mfeElement
      .onAuthorising?.({ paymentMethod })
      .then(async () => {
        DataDog.info(`onAuthorising Promise resolved`)
        return continuePayment()
      })
      .catch(() => {
        console.log('onAuthorising Promise rejected')
        DataDog.info(`onAuthorising Promise rejected`)
      })
  }
  return continuePayment()
}
